import { useState, useRef, useCallback, useEffect } from 'react'
import i18n from '../../i18n'
import styled, { useTheme } from 'styled-components'
import { swallerIcons, getSwallerColor } from './icons'

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.3)'};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 1rem;
`

const ModalBox = styled.div`
  background: ${(props) => props.theme.colors.background.primary};
  border-radius: 12px;
  padding: 2rem;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.$color};
`

const Message = styled.div`
  font-size: 1rem;
  color: ${(props) => props.theme.colors.text.secondary};
  text-align: center;
  line-height: 1.5;
`

const InputField = styled.input`
  width: 100%;
  padding: 0.625rem 0.875rem;
  font-size: 0.875rem;
  color: ${(props) => props.theme.colors.text.secondary};
  background: ${(props) => props.theme.colors.background.secondary};
  border: 1px solid ${(props) => props.theme.colors.border.medium};
  border-radius: 6px;
  outline: none;
  transition: all 0.2s ease;

  &:focus {
    border-color: ${(props) => props.theme.colors.text.primary};
    background: ${(props) => props.theme.colors.background.primary};
  }

  &::placeholder {
    color: ${(props) => props.theme.colors.text.secondary};
    opacity: 0.5;
  }
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  width: 100%;
`

const Button = styled.button`
  flex: 1;
  padding: 0.75rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const CancelButton = styled(Button)`
  background: transparent;
  color: ${(props) => props.theme.colors.text.secondary};
  border: 1px solid ${(props) => props.theme.colors.border.medium};

  &:hover:not(:disabled) {
    background: ${(props) => props.theme.colors.background.secondary};
    transform: translateY(-1px);
  }
`

const ConfirmButton = styled(Button)`
  background: ${(props) => (props.$danger ? '#DC2626' : props.theme.colors.text.primary)};
  color: ${(props) => (props.$danger ? '#ffffff' : props.theme.colors.background.primary)};

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`

export const SwallerModal = ({ type, text, placeholder, dangerMode, onClose }) => {
  const theme = useTheme()
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef(null)

  const color = getSwallerColor(type === 'input' ? 'question' : type, theme)
  const Icon = swallerIcons[type === 'input' ? 'question' : type]

  const handleCancel = useCallback(() => {
    if (type === 'question') {
      onClose(false)
    } else if (type === 'input') {
      onClose(null)
    }
  }, [type, onClose])

  const handleConfirm = useCallback(() => {
    if (type === 'question') {
      onClose(true)
    } else if (type === 'input') {
      onClose(inputValue || null)
    }
  }, [type, inputValue, onClose])

  useEffect(() => {
    // Focus input if it's an input modal
    if (type === 'input' && inputRef.current) {
      inputRef.current.focus()
    }

    // Handle ESC key
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleCancel()
      } else if (e.key === 'Enter' && type === 'input') {
        handleConfirm()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [type, handleCancel, handleConfirm])

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCancel()
    }
  }

  return (
    <Overlay data-swaller-modal onClick={handleOverlayClick}>
      <ModalBox>
        <IconWrapper $color={color}>
          {Icon && <Icon size={48} strokeWidth={2} />}
        </IconWrapper>

        <Message>{text}</Message>

        {type === 'input' && (
          <InputField
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={placeholder}
          />
        )}

        <ButtonGroup>
          <CancelButton onClick={handleCancel}>{i18n.t('cancel', { ns: 'common' })}</CancelButton>
          <ConfirmButton onClick={handleConfirm} $danger={dangerMode}>
            {i18n.t('confirm', { ns: 'common' })}
          </ConfirmButton>
        </ButtonGroup>
      </ModalBox>
    </Overlay>
  )
}
